import * as React from "react";
import { cva, type VariantProps } from "class-variance-authority";
import { cn } from "../../lib/utils";
import { FormFooter } from "../FormFooter";

const radioVariants = cva(
  "relative inline-flex items-center justify-center shrink-0 border transition-all cursor-pointer rounded-full",
  {
    variants: {
      size: {
        small: "w-[14px] h-[14px] border-[1.5px]",
        medium: "w-[16px] h-[16px] border-[1.5px]",
        large: "w-[20px] h-[20px] border-[2px]",
      },
      validationState: {
        none: "",
        error:
          "border-action-outline-negative-default hover:border-action-outline-negative-hover",
      },
      isChecked: {
        true: "",
        false: "",
      },
      isDisabled: {
        true: "cursor-not-allowed opacity-60 border-action-outline-neutral-disabled bg-surface-fill-neutral-subtle",
        false: "",
      },
      isFocused: {
        true: "",
        false: "",
      },
    },
    compoundVariants: [
      // Unchecked state - none validation
      {
        isChecked: false,
        validationState: "none",
        isDisabled: false,
        class: `border-action-outline-neutral-faded 
          hover:bg-action-fill-neutral-faded 
          hover:border-action-outline-neutral-faded
          `,
      },
      // Checked state - none validation
      {
        isChecked: true,
        validationState: "none",
        isDisabled: false,
        class:
          "bg-action-fill-primary-default hover:bg-action-fill-primary-hover border-action-fill-primary-default hover:border-action-fill-primary-hover",
      },
      // Checked state - error validation
      {
        isChecked: true,
        validationState: "error",
        isDisabled: false,
        class:
          "bg-action-fill-negative-default hover:bg-action-fill-negative-hover border-action-fill-negative-default hover:border-action-fill-negative-hover",
      },
      // Focused state - none validation
      {
        isFocused: true,
        validationState: "none",
        isDisabled: false,
        class: "ring-2 ring-action-outline-primary-faded",
      },
      // Focused state - error validation
      {
        isFocused: true,
        validationState: "error",
        isDisabled: false,
        class: "ring-2 ring-action-outline-negative-faded",
      },
    ],
    defaultVariants: {
      size: "medium",
      validationState: "none",
      isChecked: false,
      isDisabled: false,
      isFocused: false,
    },
  }
);

export interface RadioProps
  extends Omit<React.InputHTMLAttributes<HTMLInputElement>, "size"> {
  /**
   * Label text to display next to the radio button
   */
  label?: string;
  /**
   * Error text to display below the radio button
   */
  errorText?: string;
  /**
   * Size of the radio button
   */
  size?: "small" | "medium" | "large";
  /**
   * Validation state of the radio button
   */
  validationState?: "none" | "error";
  /**
   * Whether the radio button is disabled
   */
  isDisabled?: boolean;
  /**
   * Whether to show error text (defaults to true if errorText is provided)
   */
  showErrorText?: boolean;
  /**
   * Custom class name for the container
   */
  containerClassName?: string;
  /**
   * Custom class name for the label
   */
  labelClassName?: string;
}

const Radio = React.forwardRef<HTMLInputElement, RadioProps>(
  (
    {
      label,
      errorText,
      size = "medium",
      validationState = "none",
      isDisabled = false,
      showErrorText = true,
      containerClassName,
      labelClassName,
      className,
      checked,
      onChange,
      ...props
    },
    ref
  ) => {
    const [internalChecked, setInternalChecked] = React.useState(false);
    const [showRipple, setShowRipple] = React.useState(false);
    const [isFocused, setIsFocused] = React.useState(false);
    const inputRef = React.useRef<HTMLInputElement>(null);

    // Use forwarded ref or internal ref
    React.useImperativeHandle(ref, () => inputRef.current!);

    const isChecked = checked !== undefined ? checked : internalChecked;

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      if (onChange) {
        onChange(e);
      } else {
        setInternalChecked(e.target.checked);
      }
    };

    const triggerRipple = () => {
      if (!isDisabled) {
        setShowRipple(true);
        setTimeout(() => {
          setShowRipple(false);
        }, 360); // Match animation duration (0.36s)
      }
    };

    const handleContainerClick = () => {
      if (!isDisabled && inputRef.current) {
        // Only show ripple when checking (not unchecking)
        const willBeChecked = !isChecked;
        if (willBeChecked) {
          triggerRipple();
        }
        inputRef.current.click();
      }
    };

    const handleKeyDown = (e: React.KeyboardEvent) => {
      if ((e.key === " " || e.key === "Enter") && !isDisabled) {
        e.preventDefault();
        // Only show ripple when checking (not unchecking)
        const willBeChecked = !isChecked;
        if (willBeChecked) {
          triggerRipple();
        }
        inputRef.current?.click();
      }
    };

    const handleFocus = () => {
      if (!isDisabled) {
        setIsFocused(true);
      }
    };

    const handleBlur = () => {
      setIsFocused(false);
    };

    // Size-based configurations
    const sizeConfig = {
      small: {
        gap: "gap-2",
        labelSize: "text-body-small-regular",
        innerCircleSize: 6,
      },
      medium: {
        gap: "gap-2.5",
        labelSize: "text-body-medium-regular",
        innerCircleSize: 7,
      },
      large: {
        gap: "gap-3",
        labelSize: "text-body-large-regular",
        innerCircleSize: 8,
      },
    };

    const config = sizeConfig[size];

    // Determine if we should show the error text
    const shouldShowError = errorText && showErrorText;

    return (
      <div className={cn("inline-flex flex-col", containerClassName)}>
        <div
          className={cn(
            "inline-flex items-center",
            config.gap,
            isDisabled ? "cursor-not-allowed" : "cursor-pointer"
          )}
          onClick={handleContainerClick}
          onKeyDown={handleKeyDown}
          onFocus={handleFocus}
          onBlur={handleBlur}
          role="radio"
          aria-checked={isChecked}
          aria-disabled={isDisabled}
          tabIndex={isDisabled ? -1 : 0}
        >
          <input
            ref={inputRef}
            type="radio"
            className="sr-only"
            checked={isChecked}
            onChange={handleChange}
            disabled={isDisabled}
            {...props}
          />

          <div className="relative inline-flex shrink-0">
            {/* Ripple Effect */}
            {showRipple && (
              <div
                className={cn(
                  "absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 rounded-full pointer-events-none w-full h-full",
                  validationState === "error"
                    ? "bg-action-outline-negative-faded"
                    : "bg-action-outline-primary-faded"
                )}
                style={{
                  animation: "var(--animate-checkbox-ripple)",
                }}
              />
            )}

            <div
              className={cn(
                radioVariants({
                  size,
                  validationState,
                  isChecked,
                  isDisabled,
                  isFocused,
                }),
                className
              )}
            >
              {/* Inner White Circle (creates ring effect) */}
              {isChecked && (
                <div
                  className="rounded-full bg-white transition-all"
                  style={{
                    width: `${config.innerCircleSize}px`,
                    height: `${config.innerCircleSize}px`,
                  }}
                />
              )}
            </div>
          </div>

          {label && (
            <label
              className={cn(
                config.labelSize,
                "select-none inline-flex items-center",
                isDisabled
                  ? "text-surface-ink-neutral-disabled"
                  : "text-surface-ink-neutral-normal",
                labelClassName
              )}
            >
              {label}
            </label>
          )}
        </div>

        {/* Error text */}
        {shouldShowError && (
          <FormFooter
            helperText={errorText}
            validationState="negative"
            size={size}
            isDisabled={isDisabled}
          />
        )}
      </div>
    );
  }
);

Radio.displayName = "Radio";

export { Radio, radioVariants };
